import React, { useRef, Dispatch, SetStateAction } from 'react';

import {
  Drawer,
  Typography,
  IconButton,
  MuiThemeProvider,
  makeStyles
} from '@material-ui/core';
import { Form, FormValues, yupSchemaToValidationFunc } from 'components/Forms';
import { SecondaryButton, SubmitButton } from 'components/Forms/Buttons';
import { feedbackButtonZIndex } from 'styles/defaultTheme';
import { FormApi } from 'final-form';
import { useAllJobTypesData, useCapacityTypeData } from 'store/selectors';
import CloseIcon from '@material-ui/icons/Close';
import {
  editCapacityType,
  fetchCapacityTypes
} from 'store/slices/capacityTypes';
import { useDispatch } from 'react-redux';
import { AutocompleteFilterOptions } from 'components/FilterSearch/FilterSearch';
import { setSnackbar, SnackbarStatus } from 'store/slices/snackbar';
import reserveCapacityTheme from 'views/ReserveCapacity/reserveCapacityTheme';
import {
  AddCapacityTypeFormFields,
  getAddCapacityTypeFields,
  validationSchema
} from './addCapacityFormConfig';

const useDrawerStyles = makeStyles(
  theme => ({
    drawer: {
      width: theme.containers.small,
      padding: theme.spacing(4, 3, 2, 3)
    },
    header: {
      display: 'flex',
      paddingBottom: theme.spacing(4),
      alignItems: 'flex-start',
      justifyContent: 'space-between'
    },
    closeButton: {
      '& .MuiSvgIcon-root': {
        fontSize: theme.spacing(3),
        cursor: 'pointer'
      },
      padding: 0
    },
    submitButton: {
      marginLeft: theme.spacing(2)
    },
    footer: {
      display: 'flex',
      justifyContent: 'flex-end',
      paddingTop: theme.spacing(3)
    }
  }),
  { name: 'EditCapacityTypeDrawer' }
);

interface EditCapacityTypeDrawerProps {
  isOpen: boolean;
  toggleDrawer: () => void;
  capacityTypeId: string;
  setEditedId: Dispatch<SetStateAction<string | undefined>>;
}

export default function EditCapacityTypeDrawer({
  isOpen,
  toggleDrawer,
  capacityTypeId,
  setEditedId
}: EditCapacityTypeDrawerProps) {
  const drawerClasses = useDrawerStyles();
  const formRef = useRef<FormApi>();
  const dispatch = useDispatch();

  const capacityTypeToEdit = useCapacityTypeData(capacityTypeId);

  const submitForm = async (values: FormValues) => {
    if (formRef.current?.getState().dirty) {
      await dispatch(
        editCapacityType({
          id: capacityTypeId,
          name: values[AddCapacityTypeFormFields.CapacityName] as string,
          jobTypes: (values[
            AddCapacityTypeFormFields.JobType
          ] as AutocompleteFilterOptions[])?.map(({ value }) => value),
          notes: values[AddCapacityTypeFormFields.Notes] as string
        })
      );
      dispatch(fetchCapacityTypes());
      setEditedId(capacityTypeId);
    }
    toggleDrawer();
  };

  const handleDrawerClose = () => {
    const formState = formRef.current?.getState();

    if (formState?.dirty) {
      dispatch(
        setSnackbar({
          message: 'Your changes have been discarded.',
          show: true,
          status: SnackbarStatus.Success,
          useNewSnackbar: true
        })
      );
    }
    toggleDrawer();
  };

  const handleDrawerSubmit = () => formRef.current?.submit();

  const allJobTypes = useAllJobTypesData() ?? [];
  const editCapacityTypeFields = getAddCapacityTypeFields(allJobTypes);
  const initialFormValues: FormValues = capacityTypeToEdit
    ? {
        [AddCapacityTypeFormFields.CapacityName]: capacityTypeToEdit.name,
        [AddCapacityTypeFormFields.JobType]: capacityTypeToEdit.jobTypes
          .map(jobTypeId => {
            const jobType = allJobTypes.find(jt => jt.id === jobTypeId);
            return jobType
              ? {
                  label: jobType.name,
                  value: jobTypeId
                }
              : undefined;
          })
          .filter(Boolean),
        [AddCapacityTypeFormFields.Notes]: capacityTypeToEdit.notes
      }
    : {};

  return (
    <Drawer
      variant="temporary"
      anchor="right"
      open={isOpen}
      onClose={handleDrawerClose}
      classes={{
        paper: drawerClasses.drawer
      }}
      ModalProps={{
        style: {
          // This makes sure that the drawer is on top of the "Feedback" button.
          zIndex: feedbackButtonZIndex
        }
      }}
      data-testid="editCapacityTypeDrawer"
    >
      <header className={drawerClasses.header}>
        <Typography variant="h1">Edit capacity type</Typography>
        <IconButton
          aria-label="close"
          onClick={handleDrawerClose}
          className={drawerClasses.closeButton}
          size="medium"
        >
          <CloseIcon fontSize="medium" />
        </IconButton>
      </header>
      <MuiThemeProvider theme={reserveCapacityTheme}>
        <Form
          validationFunc={yupSchemaToValidationFunc(validationSchema)}
          fields={editCapacityTypeFields}
          onSubmit={submitForm}
          goBackToPreviousPage={false}
          initialValues={initialFormValues}
        >
          {({ form }) => {
            formRef.current = form;
          }}
        </Form>
      </MuiThemeProvider>
      <footer className={drawerClasses.footer}>
        <SecondaryButton
          size="medium"
          onClick={handleDrawerClose}
          testId="editCapacityTypeCancel"
        >
          Cancel
        </SecondaryButton>
        <SubmitButton
          size="medium"
          onClick={handleDrawerSubmit}
          testId="editCapacityTypeSubmit"
          className={drawerClasses.submitButton}
        >
          Save
        </SubmitButton>
      </footer>
    </Drawer>
  );
}
